import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Spin, Alert } from 'antd';
import usePermission from '../../hooks/usePermission';
import { matrisiGetir, yolYetkisi, yolaGirebilir } from '../../utils/yetkiMatris';

/**
 * Rota kapısı.
 *
 * 25.08 md madde 6'ya kadar: `tip`/`anahtar` prop'u verilmeyen rotalar
 * (55'in 52'si) yalnızca oturum kontrol ediyordu — yani "yetki yok" değil
 * "yetki sorulmuyor" durumu vardı. Artık prop verilmediğinde gereken yetki
 * MERKEZÎ MATRİSTEN (`/yetki/matris`) geçerli yola göre çözülür; böylece
 * App.js'te 52 satır düzenlemeden tüm ekranlar kapıya bağlanır ve menü
 * süzmesi/API middleware'i ile aynı tabloyu kullanır.
 */
const PermissionBasedRoute = ({
  children,
  tip,
  anahtar,
  permissionType = 'r',
  requireAuth = true,
  fallback = null
}) => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const location = useLocation();
  const { hasPermission, loading: permissionLoading } = usePermission(tip, anahtar, permissionType);

  // Prop verilmediyse matrise bak.
  const acikProp = Boolean(tip && anahtar);
  const [matris, setMatris] = useState(undefined); // undefined = yükleniyor

  useEffect(() => {
    if (acikProp) return undefined;
    let iptal = false;
    matrisiGetir().then((m) => { if (!iptal) setMatris(m); });
    return () => { iptal = true; };
  }, [acikProp]);

  const matrisYukleniyor = !acikProp && matris === undefined;

  if (authLoading || (acikProp && permissionLoading) || matrisYukleniyor) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (requireAuth && !isAuthenticated()) {
    return <Navigate to="/giris" state={{ from: location }} replace />;
  }

  const matrisGerekli = !acikProp ? yolYetkisi(matris, location.pathname) : null;
  const izinVar = acikProp
    ? hasPermission
    : yolaGirebilir(matris, location.pathname);

  if (!izinVar) {
    if (fallback) {
      return fallback;
    }

    const gosterTip = acikProp ? tip : matrisGerekli?.tip;
    const gosterAnahtar = acikProp ? anahtar : matrisGerekli?.anahtar;
    const gosterTur = acikProp ? permissionType : matrisGerekli?.tur;

    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="Erişim Engellendi"
          description={
            <div>
              <p>Bu sayfaya erişim için gerekli yetkiniz bulunmamaktadır.</p>
              <p>
                <strong>Gerekli Yetki:</strong> {gosterTip} / {gosterAnahtar} ({gosterTur === 'r' ? 'Okuma' : 'Yazma'})
              </p>
              {user?.pozisyon ? (
                <p>
                  <strong>Mevcut Pozisyonunuz:</strong> {user.pozisyon}
                </p>
              ) : (
                <p>
                  Kullanıcınıza <strong>pozisyon tanımlanmamış</strong>. Yetkiler pozisyon
                  üzerinden verildiği için önce organizasyon şemasından pozisyon atanmalı.
                </p>
              )}
            </div>
          }
          type="error"
          showIcon
        />
      </div>
    );
  }

  return children;
};

export default PermissionBasedRoute;
